import React, { useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Slider,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';

import { USER_LOAN_PROGRAM, userLoanProgramMap } from '@/config/constants';
import {
  PROGRAM_KEY_BY_USER_PROGRAM,
  useHybridInterestRate,
} from '@/hooks/useHybridInterestRate';
import { selectRateSheet } from '@/store/slices/hybridInterestRateSlice';

interface HybridInterestRateDisplayProps {
  loanProgram: USER_LOAN_PROGRAM;
  creditScore?: number;
  ltv?: number;
  /** Bridge: term in months (6/12/18/24) for admin tier pricing */
  bridgeTermMonths?: number;
  /**
   * Shows the manual base/credit/LTV/custom-rate override sliders.
   * useHybridInterestRate.calculateAdjustedRate's own code comment is explicit
   * that backend-controlled rates should not be hand-edited outside admin
   * tooling — default false so a regular user can't silently invalidate their
   * own estimate (LAUNCH UPDATES.md 3.5/C16). Admin surfaces opt in.
   */
  showAdjustments?: boolean;
}

export const HybridInterestRateDisplay: React.FC<
  HybridInterestRateDisplayProps
> = ({
  loanProgram,
  creditScore = 700,
  ltv = 80,
  bridgeTermMonths,
  showAdjustments = false,
}) => {
  const {
    backendRates,
    localAdjustments,
    creditScoreAdjustments,
    ltvAdjustments,
    needsRefresh,
    isStale,
    loading,
    error,
    setAdjustment,
    resetAdjustments,
    refreshRates,
    calculateAdjustedRate,
  } = useHybridInterestRate();

  const [showAdvanced, setShowAdvanced] = useState(false);

  const rateSheet = useSelector(selectRateSheet);
  const isCash = loanProgram === USER_LOAN_PROGRAM.CASH;
  const programKey = PROGRAM_KEY_BY_USER_PROGRAM[loanProgram] || 'conventional';
  const rawBase =
    rateSheet?.programs?.[programKey]?.baseRate ??
    backendRates[programKey as keyof typeof backendRates];
  const baseRate =
    typeof rawBase === 'number' && Number.isFinite(rawBase) ? rawBase : null;
  const programLabel =
    userLoanProgramMap[loanProgram as keyof typeof userLoanProgramMap] ||
    loanProgram;
  const adjustments = localAdjustments[loanProgram];
  const effectiveRate = calculateAdjustedRate(
    loanProgram,
    creditScore,
    ltv,
    loanProgram === USER_LOAN_PROGRAM.BRIDGE
      ? { bridgeTermMonths: bridgeTermMonths ?? 12 }
      : undefined
  );

  // Handle local adjustments
  const handleBaseAdjustment = (value: number) => {
    setAdjustment(loanProgram, { baseAdjustment: value });
  };

  const handleCreditScoreAdjustment = (value: number) => {
    setAdjustment(loanProgram, { creditScoreAdjustment: value });
  };

  const handleLTVAdjustment = (value: number) => {
    setAdjustment(loanProgram, { ltvAdjustment: value });
  };

  const handleCustomRate = (value: number) => {
    setAdjustment(loanProgram, { customRate: value });
  };

  const handleReset = () => {
    resetAdjustments(loanProgram);
  };

  // Format rate for display
  const formatRate = (rate: number | null) =>
    rate === null || !Number.isFinite(rate) ? '—' : `${rate.toFixed(3)}%`;

  const formatLastUpdated = (value?: string | null) => {
    if (!value) return 'Not yet updated';
    // Plain YYYY-MM-DD (market date) must not shift a day in US time zones.
    const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(`${value}T12:00:00`)
      : new Date(value);
    if (Number.isNaN(date.getTime())) return 'Not yet updated';
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Rates are only ever this stale/needing-refresh internally for a few minutes
  // (see useHybridInterestRate's 5-minute poll) — a plain user doesn't need to
  // see cache-implementation state, only whether the number can be trusted.
  const showsCurrentRate = !isStale && !needsRefresh;

  return (
    <Card sx={{ p: 3, mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6" component="h3">
          Interest Rate: {programLabel}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!showsCurrentRate && (
            <Chip label="Updating…" color="warning" size="small" />
          )}
          <Tooltip title="Refresh rates">
            <span>
              <IconButton
                onClick={refreshRates}
                disabled={loading}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {typeof error === 'string'
            ? error
            : 'An error occurred while loading interest rates'}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {isCash ? (
        <Typography variant="body2" color="text.secondary">
          Cash purchase — no mortgage rate applies. Switch this property to a
          loan program to see today&apos;s rates.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {/* Base Rate Display */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Base rate
              </Typography>
              <Typography variant="h4" color="primary">
                {formatRate(baseRate)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {rateSheet?.marketAsOf
                  ? `Market data as of ${formatLastUpdated(
                      rateSheet.marketAsOf
                    )}`
                  : `Updated ${formatLastUpdated(backendRates.lastUpdated)}`}
              </Typography>
            </Box>
          </Grid>

          {/* Effective Rate Display */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Effective Rate
              </Typography>
              <Typography variant="h4" color="secondary">
                {formatRate(effectiveRate)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                For your credit score and loan-to-value
              </Typography>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Adjustments Section */}
      {showAdjustments && (
        <Box sx={{ mt: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="subtitle1">Rate adjustments</Typography>
            <Button size="small" onClick={() => setShowAdvanced(!showAdvanced)}>
              {showAdvanced ? 'Hide' : 'Show'} Advanced
            </Button>
          </Box>

          {showAdvanced && (
            <Grid container spacing={2}>
              {/* Base Adjustment */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  Base Rate Adjustment: {adjustments?.baseAdjustment || 0}%
                </Typography>
                <Slider
                  value={adjustments?.baseAdjustment || 0}
                  onChange={(_, value) => handleBaseAdjustment(value as number)}
                  min={-2}
                  max={2}
                  step={0.125}
                  marks
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}%`}
                />
              </Grid>

              {/* Credit Score Adjustment */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  Credit Score Adjustment:{' '}
                  {adjustments?.creditScoreAdjustment || 0}%
                </Typography>
                <Slider
                  value={adjustments?.creditScoreAdjustment || 0}
                  onChange={(_, value) =>
                    handleCreditScoreAdjustment(value as number)
                  }
                  min={-1}
                  max={1}
                  step={0.125}
                  marks
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}%`}
                />
              </Grid>

              {/* LTV Adjustment */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  LTV Adjustment: {adjustments?.ltvAdjustment || 0}%
                </Typography>
                <Slider
                  value={adjustments?.ltvAdjustment || 0}
                  onChange={(_, value) => handleLTVAdjustment(value as number)}
                  min={-0.5}
                  max={0.5}
                  step={0.125}
                  marks
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}%`}
                />
              </Grid>

              {/* Custom Rate */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  Custom Rate (overrides all adjustments)
                </Typography>
                <TextField
                  type="number"
                  value={adjustments?.customRate || ''}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    handleCustomRate(isNaN(value) ? 0 : value);
                  }}
                  placeholder="Enter custom rate"
                  size="small"
                  inputProps={{
                    step: 0.125,
                    min: 0,
                    max: 20,
                  }}
                />
              </Grid>
            </Grid>
          )}

          {/* Adjustment Summary */}
          {adjustments &&
            Object.values(adjustments).some((val) => val !== 0) && (
              <Box
                sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}
              >
                <Typography variant="body2" gutterBottom>
                  <strong>Current Adjustments:</strong>
                </Typography>
                <Grid container spacing={1}>
                  {adjustments.baseAdjustment !== 0 && (
                    <Grid item xs={6}>
                      <Chip
                        label={`Base: ${
                          adjustments.baseAdjustment > 0 ? '+' : ''
                        }${adjustments.baseAdjustment}%`}
                        color={
                          adjustments.baseAdjustment > 0 ? 'success' : 'error'
                        }
                        size="small"
                      />
                    </Grid>
                  )}
                  {adjustments.creditScoreAdjustment !== 0 && (
                    <Grid item xs={6}>
                      <Chip
                        label={`Credit: ${
                          adjustments.creditScoreAdjustment > 0 ? '+' : ''
                        }${adjustments.creditScoreAdjustment}%`}
                        color={
                          adjustments.creditScoreAdjustment > 0
                            ? 'success'
                            : 'error'
                        }
                        size="small"
                      />
                    </Grid>
                  )}
                  {adjustments.ltvAdjustment !== 0 && (
                    <Grid item xs={6}>
                      <Chip
                        label={`LTV: ${
                          adjustments.ltvAdjustment > 0 ? '+' : ''
                        }${adjustments.ltvAdjustment}%`}
                        color={
                          adjustments.ltvAdjustment > 0 ? 'success' : 'error'
                        }
                        size="small"
                      />
                    </Grid>
                  )}
                  {adjustments.customRate !== undefined && (
                    <Grid item xs={6}>
                      <Chip
                        label={`Custom: ${adjustments.customRate}%`}
                        color="warning"
                        size="small"
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

          {/* Action Buttons */}
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleReset}
              disabled={
                !adjustments ||
                Object.values(adjustments).every((val) => val === 0)
              }
            >
              Reset Adjustments
            </Button>
          </Box>
        </Box>
      )}

      {/* Plain-language explanation of why the effective rate differs from the base rate. */}
      {(creditScoreAdjustments || ltvAdjustments) && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2">
            Your rate reflects your credit score and loan-to-value ratio. Higher
            credit scores and lower loan-to-value typically qualify for a lower
            rate than the base rate shown above.
          </Typography>
        </Box>
      )}
    </Card>
  );
};
